// =============================================================================
// ESPOLIDAR WEB ADMIN - DEVICES PAGE
// =============================================================================

import { Link } from 'react-router-dom'
import { useDevices, useDeleteDevice } from '../hooks/useDevices'
import { Cpu, Wifi, WifiOff, Settings, Trash2, Package } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import { useState } from 'react'

export default function DevicesPage() {
  const { data: devices, isLoading } = useDevices()
  const deleteDevice = useDeleteDevice()
  const [filter, setFilter] = useState<'all' | 'online' | 'offline'>('all')

  const filteredDevices = devices?.filter((device) => {
    if (filter === 'all') return true
    return device.status === filter
  })

  const handleDelete = (deviceId: string) => {
    if (confirm('Sei sicuro di voler eliminare questo dispositivo?')) {
      deleteDevice.mutate(deviceId)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dispositivi</h1>
        <div className="flex items-center gap-4">
          {/* Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            className="input w-auto"
          >
            <option value="all">Tutti</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </select>
        </div>
      </div>

      {filteredDevices && filteredDevices.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDevices.map((device) => (
            <div
              key={device.id}
              className="bg-white rounded-xl shadow-sm overflow-hidden card-hover"
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        device.status === 'online'
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      <Cpu size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{device.name}</h3>
                      <p className="text-sm text-gray-500">{device.device_id}</p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      device.status === 'online'
                        ? 'bg-green-100 text-green-700'
                        : device.status === 'offline'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {device.status === 'online' ? (
                      <Wifi size={12} />
                    ) : (
                      <WifiOff size={12} />
                    )}
                    {device.status}
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="p-4 space-y-2">
                {device.description && (
                  <p className="text-sm text-gray-600">{device.description}</p>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Ultimo contatto:</span>
                  <span className="text-gray-700">
                    {device.last_seen
                      ? format(new Date(device.last_seen), 'dd/MM/yyyy HH:mm:ss', { locale: it })
                      : 'Mai'}
                  </span>
                </div>
                {device.last_ip && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">IP:</span>
                    <span className="text-gray-700 font-mono">{device.last_ip}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-1">
                    <Package size={12} />
                    Versione:
                  </span>
                  <span className={`font-mono ${device.software_version ? 'text-gray-700' : 'text-gray-400'}`}>
                    {device.software_version || 'N/A'}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                <Link
                  to={`/devices/${device.device_id}`}
                  className="flex items-center gap-2 text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  <Settings size={16} />
                  Configura
                </Link>
                <button
                  onClick={() => handleDelete(device.device_id)}
                  className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm"
                  disabled={deleteDevice.isPending}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Cpu size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Nessun dispositivo
          </h3>
          <p className="text-gray-500 mb-6">
            I dispositivi appariranno qui quando si registreranno al server.
          </p>
        </div>
      )}
    </div>
  )
}
